import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Input, Pagination, Select, Tabs } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { dateNumeric, humanise, money, number, relative } from '../../lib/format.js';

/**
 * What is happening between tenants, owners and brokers — read-only.
 *
 *   - Requirements: what tenants have posted they are looking for.
 *   - Leads: a requirement offered to a matching broker, and whether they paid
 *     to unlock it.
 *   - Owner enquiries: an owner asking a broker to let their flat.
 *
 * Nobody's phone number is on these lists: the people involved own those
 * conversations, and the admin needs the shape of the market, not the numbers.
 */

const TABS = [
  { value: 'requirements', label: 'Requirements' },
  { value: 'leads', label: 'Leads' },
  { value: 'enquiries', label: 'Owner enquiries' },
];

function budget(min, max) {
  if (!max) return '—';
  return min ? `${money(min)} – ${money(max)}` : `up to ${money(max)}`;
}

function bedrooms(min, max) {
  if (min == null && max == null) return null;
  if (min === max || max == null) return `${min} BHK`;
  return `${min ?? 1}–${max} BHK`;
}

function StatusFilter({ value, onChange, options }) {
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)} aria-label="Filter by status">
      <option value="">Any status</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {humanise(option)}
        </option>
      ))}
    </Select>
  );
}

function RequirementsTab() {
  const list = useListQuery({
    key: 'admin-requirements',
    fetcher: api.broker.requirements,
    initialFilters: { status: '' },
    searchable: true,
  });

  const columns = [
    {
      key: 'tenant',
      header: 'Tenant',
      render: (row) =>
        row.tenant ? (
          <Link className="table__link table__primary" to={`/users/${row.tenant._id}`}>
            {row.tenant.name || 'Unnamed'}
          </Link>
        ) : (
          <span className="table__muted">Deleted account</span>
        ),
    },
    {
      key: 'where',
      header: 'Where',
      render: (row) => (
        <div>
          <div>{row.location?.city}</div>
          <div className="table__muted">{(row.location?.localities || []).join(', ')}</div>
        </div>
      ),
    },
    {
      key: 'what',
      header: 'Looking for',
      render: (row) => (
        <div>
          <div>{budget(row.budgetMinMinor, row.budgetMaxMinor)}</div>
          <div className="table__muted">
            {[bedrooms(row.bedroomsMin, row.bedroomsMax), row.moveInDate && `from ${row.moveInDate}`].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      key: 'leads',
      header: 'Brokers',
      className: 'table__numeric',
      render: (row) => `${number(row.leadCount || 0)} of ${number(row.maxLeads || 0)}`,
    },
    { key: 'status', header: 'Status', render: (row) => <Badge status={row.status} /> },
    {
      key: 'expires',
      header: 'Expires',
      render: (row) => <span className="table__muted nowrap">{dateNumeric(row.expiresAt)}</span>,
    },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <div className="toolbar__search">
            <Input
              placeholder="Search by city or locality"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              aria-label="Search requirements"
            />
          </div>
          <StatusFilter
            value={list.filters.status}
            onChange={(value) => list.setFilter('status', value)}
            options={['active', 'paused', 'fulfilled', 'expired', 'cancelled']}
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        empty={{ title: 'No requirements', body: 'Nothing matches these filters. Tenants post requirements from the rental app.' }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}

function LeadsTab() {
  const list = useListQuery({ key: 'admin-leads', fetcher: api.broker.leads, initialFilters: { status: '' } });

  const columns = [
    {
      key: 'broker',
      header: 'Broker',
      render: (row) =>
        row.broker ? (
          <Link className="table__link table__primary" to={`/brokers/${row.broker._id}`}>
            {row.broker.businessName}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      render: (row) => (
        <div>
          <div>{row.tenant?.name || 'Deleted account'}</div>
          <div className="table__muted">
            {row.requirement
              ? `${row.requirement.location?.city || ''} · ${budget(row.requirement.budgetMinMinor, row.requirement.budgetMaxMinor)}`
              : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'score',
      header: 'Match',
      className: 'table__numeric',
      render: (row) => (row.matchScore != null ? `${number(row.matchScore)}%` : '—'),
    },
    {
      key: 'unlock',
      header: 'Unlocked',
      render: (row) =>
        row.unlockedAt ? (
          <span>
            {number(row.creditCost)} credits <span className="table__muted">· {relative(row.unlockedAt)}</span>
          </span>
        ) : (
          <span className="table__muted">Not yet ({number(row.creditCost)} credits)</span>
        ),
    },
    { key: 'status', header: 'Status', render: (row) => <Badge status={row.status} /> },
    { key: 'when', header: 'Offered', render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span> },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <StatusFilter
            value={list.filters.status}
            onChange={(value) => list.setFilter('status', value)}
            options={['new', 'viewed', 'contacted', 'interested', 'visit_scheduled', 'negotiating', 'won', 'lost']}
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        empty={{ title: 'No leads', body: 'Leads appear when a tenant’s requirement matches a broker’s areas.' }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}

function EnquiriesTab() {
  const list = useListQuery({ key: 'admin-enquiries', fetcher: api.broker.enquiries, initialFilters: { status: '' } });

  const columns = [
    {
      key: 'owner',
      header: 'Owner',
      render: (row) =>
        row.owner ? (
          <Link className="table__link table__primary" to={`/users/${row.owner._id}`}>
            {row.owner.name || 'Unnamed'}
          </Link>
        ) : (
          <span className="table__muted">Deleted account</span>
        ),
    },
    {
      key: 'flat',
      header: 'Flat',
      render: (row) => (
        <div>
          <div>{row.property?.title}</div>
          <div className="table__muted">
            {[row.property?.address?.locality, row.property?.address?.city].filter(Boolean).join(', ')}
            {row.property?.expectedRentMinor ? ` · ${money(row.property.expectedRentMinor)}/month` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'broker',
      header: 'Sent to',
      render: (row) =>
        row.broker ? <Link className="table__link" to={`/brokers/${row.broker._id}`}>{row.broker.businessName}</Link> : '—',
    },
    {
      key: 'unlock',
      header: 'Unlocked',
      render: (row) =>
        row.unlockedAt ? `${number(row.creditCost || 0)} credits` : <span className="table__muted">Not yet</span>,
    },
    { key: 'status', header: 'Status', render: (row) => <Badge status={row.status} /> },
    { key: 'when', header: 'Sent', render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span> },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <StatusFilter
            value={list.filters.status}
            onChange={(value) => list.setFilter('status', value)}
            options={['new', 'viewed', 'accepted', 'converted', 'declined', 'withdrawn']}
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        empty={{ title: 'No enquiries', body: 'Owners send these from the rental app when they pick a broker to let their flat.' }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}

export default function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = TABS.some((entry) => entry.value === requested) ? requested : TABS[0].value;

  return (
    <Page title="Activity" subtitle="Requirements, leads and owner enquiries across both apps. Read-only.">
      <Tabs
        tabs={TABS}
        value={tab}
        label="Activity views"
        onChange={(value) => setParams(value === TABS[0].value ? {} : { tab: value })}
      />
      {tab === 'leads' ? <LeadsTab /> : tab === 'enquiries' ? <EnquiriesTab /> : <RequirementsTab />}
    </Page>
  );
}
